import * as React from 'react';
import { useCallback, useState } from 'react';

import { InsuranceProgramFragment } from '@websky/graphql';
import { CartServiceInsurance, OnDeleteItemType } from '../../../Cart/types';
import { InsurancePageModal, InsuranceUpsaleModal } from '../../../InsurancePage';
import { useTheme } from '../../../theme';
import { useInsuranceGroupTexts, useInsuranceUpsale } from './hooks';
import SimpleLoader from '../../../SimpleLoader';
import Modal from '../../../Modal';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import ServiceSummaryOption from '../../../ServiceSummary/components/ServiceSummaryOption';
import InsuranceSummary from '../../../ServiceSummary/services/InsuranceSummary';
import CardExtraContent from '../../../CardExtra/components/CardExtraContent/CardExtraContent';
import { executeInsuranceDownload } from '@shared/lib/insurance';
import Button from '../../../BaseComponents/Button';
import { useTranslation } from 'react-i18next';

export interface Props {
	insurancePrograms?: InsuranceProgramFragment[];
	groupName?: string;
	oldVersion?: boolean;
}

const InsuranceUpsale: React.FC<Props> = ({ insurancePrograms, oldVersion, groupName }) => {
	const { InsuranceUpsale: theme } = useTheme('CheckoutAdditionalService');

	const { t } = useTranslation('Checkout');

	const model = useInsuranceUpsale(insurancePrograms);
	const groupTexts = useInsuranceGroupTexts(groupName, insurancePrograms);
	const [isDownloadLoading, setIsDownloadLoading] = useState(false);

	const handleDownloadPolicy = useCallback(async () => {
		if (!model.singleInsuranceDownload || isDownloadLoading) {
			return;
		}

		setIsDownloadLoading(true);

		try {
			await executeInsuranceDownload(model.singleInsuranceDownload);
		} catch (error) {
			console.error('Error downloading insurance policy: ', error);
		} finally {
			setIsDownloadLoading(false);
		}
	}, [model.singleInsuranceDownload, isDownloadLoading]);

	const onServiceDelete: OnDeleteItemType = async (_, service) => {
		const insuranceService = service as CartServiceInsurance;

		await model.onDeleteInsurance(insuranceService.code);
	};

	return (
		<>
			<CardExtraContent
				type={'insurance'}
				title={groupTexts.title}
				minPrice={model.minPrice}
				totalPrice={model.totalPrice}
				isSelected={model.isSelected}
				isReadonly={model.isReadonly}
				description={groupTexts.descr}
				options={groupTexts.options}
				content={
					model.isSelected ? (
						<InsuranceSummary
							selectedInsurances={model.selectedPrograms}
							onServiceDelete={model.isReadonly || model.totalPrice.amount === 0 ? null : onServiceDelete}
							isConfirmed={model.isConfirmed}
							isDownload
							onDownloadVskInsurance={model.onDownloadVskInsurance}
						>
							{items => (
								<ServiceSummaryDivider>
									<ServiceSummaryOption serviceType="Insurance" groupOption={{ items }} />
								</ServiceSummaryDivider>
							)}
						</InsuranceSummary>
					) : null
				}
				isLoading={model.isLoading}
				onChangeService={
					model.isSingleToggleMode
						? model.isSelected
							? model.onDeleteSingle
							: model.onAddSingle
						: model.open
				}
				onToggleService={
					model.isSingleToggleMode ? (model.isSelected ? model.onDeleteSingle : model.onAddSingle) : null
				}
				hideFooterControls={!!model.singleInsuranceDownload}
				onViewLoyaltyStatus={() => null}
				additionalFooterControls={
					model.singleInsuranceDownload && (
						<Button onClick={handleDownloadPolicy} variant="optionEdit" isLoading={isDownloadLoading}>
							{t('Download policy')}
						</Button>
					)
				}
			/>

			<Modal classes={{ scrollBody: theme.scrollBody, paper: theme.paper }} open={model.isOpen} fullScreen>
				<InsurancePageModal
					availablePrograms={model.availablePrograms}
					onServicesChanged={model.setIsNeedToSendSaveReq}
					onTotalInsurancePriceChange={model.setInsurancePriceToPay}
					onSaveOrderServicesParamsChange={model.setSaveOrderServicesParams}
					onDownloadNasInsurance={model.onDownloadVskInsurance}
					isShowImportantInsurancesError={model.isShowImportantInsurancesError}
					totalPrice={model.insurancePriceToPay}
					onConfirm={model.onConfirm}
					onClose={model.onClose}
				/>
				{model.isLoading && <SimpleLoader />}
			</Modal>

			<InsuranceUpsaleModal
				insurance={model.priorityInsurance}
				isOpen={model.isUpsaleOpen}
				onAgree={model.handleAgree}
				onDisagree={model.handleDisagree}
			/>
		</>
	);
};

export default InsuranceUpsale;
